import React, { useEffect, useMemo, useState } from 'react';
import { canRequest, channelColorMap, transStudioAbbreviation, useToOpinionPage } from '../utils';
import { getStudioLanguageChannel } from '@op/services';
import { useIntl, getLocale } from 'umi';
import { getPercentageInfo } from '@op/utils';
import { chartAllColors } from '@op/constants';
import { channelInfo } from '@op/components/ChannelLabel';
import CardCommon from '@op/components/CardCommon';
import SvgIcon from '@home/components/SvgIcon';
import { LANGUAGE_EN } from '@home/_config/config';
import CommonBubble from '@home/components/CommonBubble';
import { tipsLanguageContentMap } from '../_config/config';
import classNames from 'classnames';
import './index.scss';

interface IProps {
  filter: any;
  config?: {
    className?: string;
    style?: React.CSSProperties;
    listLength?: number;
    stateHeight?: number;
  };
}
export default function LanguageChannel(props: IProps ) {
  const { filter, config } = props || {};
  const { className, style, listLength = 5, stateHeight = 140} = config || {};

  const [channelList, setChannelList] = useState([]);
  const [languageList, setLanguageList] = useState([]);
  const [loading, setLoading] = useState(false);
  const isEn = getLocale() === LANGUAGE_EN;

  function requestServerData() {
    setLoading(true);
    getStudioLanguageChannel(filter)
      .then((data: any) => {
        const { channel_list, language_list } = data || {};
        setChannelList(channel_list || []);
        setLanguageList(language_list || []);
      }).catch(() => {}).finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (canRequest(filter)) {
      requestServerData();
    }
  }, [filter]);

  const transLanguage = (value: string) => transStudioAbbreviation(value, isEn ? 'lang_en' : 'lang_cn');

  return (
    <div className={classNames("dbh-studio-language-channel", className)} style={style}>
      <RenderList
        formatName={transLanguage}
        list={languageList}
        listLength={listLength}
        loading={loading}
        filter={filter}
        title="Language"
        stateHeight={stateHeight}
      ></RenderList>
      <RenderList
        filter={filter}
        formatName={(value: any) => channelInfo(value).name}
        loading={loading}
        title="Channel"
        list={channelList}
        listLength={listLength}
        className="dbh-studio-language--channel"
        stateHeight={stateHeight}
      ></RenderList>
    </div>
  );
}

function RenderList({
  title,
  className,
  list,
  formatName,
  loading,
  filter,
  listLength,
  stateHeight

}: {
  title: string;
  className?: string;
  list: Array<{ name: string; count: number; }>;
  formatName: (value: string, lang: string) => string;
  loading?: boolean;
  filter: any;
  listLength: number;
  stateHeight: number;
}) {
  const { formatMessage } = useIntl();

  const { renderList, empty } = useMemo(() => {
    const lang = getLocale();
    const total = list?.reduce((pre, cur) => pre + cur.count, 0);

    const renderList = (list || []).map((item, index) => {
      const color = channelColorMap[item.name] || chartAllColors[index];
      return {
        name: formatName(item.name, lang),
        percent: getPercentageInfo(item.count, total, 2),
        color,
      };
    });

    return {
      total,
      renderList: renderList.slice(0, listLength),
      empty: renderList.length === 0,
    };
  }, [list, formatMessage]);

  const { goToOtherPage } = useToOpinionPage({
    filter,
    key: title,
  });

  const canJump = !(loading || empty);

  return (
    <div
      className={`dbh-studio-language ${className}`}
      onClick={() => {
        if (!canJump) return;
        goToOtherPage();
      }}
    >
      <div className="dbh-studio-language-header">
        <div className="dbh-studio-language-title">
          {formatMessage({ id: title })}
        </div>
        {canJump && (
          <SvgIcon type="studio-arrow" className="dbh-trend-card-title-icon" />
        )}
      </div>
      <div className="dbh-studio-language-content">
        <CardCommon
          contentCard
          loading={loading}
          loadingHeight={stateHeight}
          hasEmptyDesc={false}
          emptyHeight={stateHeight}
          empty={empty}
        >
          {renderList.map((item, index) => (
            <div className="dbh-studio-progress" key={index + item.name}>
              <div className="dbh-studio-progress-item">
                <CommonBubble content={tipsLanguageContentMap?.[item?.name]}>
                  <div className="dbh-studio-progress-item-type">
                    {item.name}
                  </div>
                </CommonBubble>
                <div className="dbh-studio-progress-item-line">
                  <div
                    className="dbh-studio-progress-item-line-inner"
                    style={{
                      width: item.percent,
                      backgroundColor: item.color,
                    }}
                  ></div>
                </div>
                <div className="dbh-studio-progress-item-num">
                  {item.percent}
                </div>
              </div>
            </div>
          ))}
        </CardCommon>
      </div>
    </div>
  );
}
